import { supabase } from '@/lib/supabase';
import { logLoginDebug } from '@/services/loginDebug';
import { functionsBaseUrl, supabaseAnonKey } from '@/lib/supabaseConfig';

const FUNCTIONS_BASE = functionsBaseUrl;

// Tipagem dos metadados do cliente enviados para debug server-side
export interface ClientMeta {
  tz: string;
  lang: string;
  vw: number;
  vh: number;
  sw: number;
  sh: number;
  platform: string;
  deviceMemory: number | null;
  hardwareConcurrency: number | null;
  connectionType: string | null;
  ua: string;
  url: string;
  appVersion: string;
  deployId: string;
}

// ========== TOKEN MANAGEMENT (SUPABASE AUTH) ==========

/**
 * Obtém o JWT atual do Supabase Auth (sempre fresco).
 */
export async function getFreshJwt(): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) return session.access_token;
  } catch {
    // supabase auth unavailable
  }
  return null;
}

function clearAdminSession() {
  // Apenas limpa cache local de perfil (não afeta autenticação)
  localStorage.removeItem('admin_user');
  // A sessão Supabase expira naturalmente via cookie JWT
}

async function adminHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: supabaseAnonKey,
  };

  const jwt = await getFreshJwt();
  if (!jwt) throw new Error('Sua sessão expirou. Faça login novamente para continuar.');
  headers['Authorization'] = `Bearer ${jwt}`;

  return headers;
}

async function adminFetch(method: string, path: string, body?: unknown): Promise<any> {
  const url = `${FUNCTIONS_BASE}/${path}`;
  const headers = await adminHeaders();

  const options: Record<string, any> = {
    method,
    headers,
  };

  if (body && (method === 'POST' || method === 'PUT' || method === 'DELETE')) {
    options.body = JSON.stringify(body);
  }

  const res = await fetch(url, options);

  if (res.status === 401) {
    clearAdminSession();
    throw new Error('Sua sessão expirou. Faça login novamente para continuar.');
  }

  const rawText = await res.text();

  let json: any;
  try {
    json = JSON.parse(rawText);
  } catch {
    throw new Error(`Resposta inválida do servidor. Status: ${res.status}`);
  }

  if (!res.ok || !json?.success) {
    const partes: string[] = [];
    if (json?.error) partes.push(json.error);
    if (json?.code) partes.push(`code: ${json.code}`);
    if (json?.details) partes.push(`details: ${json.details}`);
    if (!partes.length) partes.push(`Erro no servidor (${res.status}). Tente novamente.`);
    throw new Error(partes.join(' | '));
  }

  return json;
}

// ========== LOGIN (usa Supabase Auth com fallback via Edge) ==========
export async function adminLoginViaEdge(
  email: string,
  password: string,
  forceLogin: boolean = false,
  requestId?: string,
  clientMeta?: ClientMeta,
): Promise<{ token: string; jwt?: string; refreshToken?: string; user: { id: string; email: string; name: string; role: string; profile?: string } }> {
  const body: Record<string, any> = { email, password };
  if (forceLogin) body.forceLogin = true;
  if (requestId) body.request_id = requestId;
  if (clientMeta) body.client_meta = clientMeta;

  const rid = requestId || crypto.randomUUID();
  const edgeStart = performance.now();

  logLoginDebug(rid, { step: 'EDGE_FUNCTION_START' });

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000);

  const res = await fetch(`${FUNCTIONS_BASE}/admin-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: supabaseAnonKey },
    body: JSON.stringify(body),
    signal: controller.signal,
  });

  clearTimeout(timeoutId);

  // Registra status HTTP da resposta da Edge Function para diagnóstico
  logLoginDebug(rid, {
    step: 'EDGE_FUNCTION_RESPONSE',
    status: res.status,
    durationMs: Math.round(performance.now() - edgeStart),
  });

  let rawText: string;
  try {
    rawText = await res.text();
  } catch (textErr: any) {
    logLoginDebug(rid, {
      step: 'EDGE_FUNCTION_FAIL',
      errorType: 'TEXT_READ_ERROR',
      message: textErr.message || 'Erro ao ler corpo da resposta',
      status: res?.status,
      durationMs: Math.round(performance.now() - edgeStart),
    });
    throw new Error('Erro ao ler resposta do servidor.');
  }

  let json: any;
  try {
    json = JSON.parse(rawText);
  } catch {
    logLoginDebug(rid, {
      step: 'EDGE_FUNCTION_FAIL',
      errorType: 'JSON_PARSE_ERROR',
      message: `Resposta nao-JSON (${rawText?.slice(0, 100) || 'vazia'})`,
      status: res.status,
      durationMs: Math.round(performance.now() - edgeStart),
    });
    throw new Error('Resposta inválida do servidor. Tente novamente.');
  }

  if (!json.success) {
    logLoginDebug(rid, {
      step: 'EDGE_FUNCTION_FAIL',
      errorType: 'EDGE_ERROR',
      message: json.error || '(sem mensagem da Edge)',
      status: res.status,
      durationMs: Math.round(performance.now() - edgeStart),
    });
    throw new Error(json.error || 'Credenciais invalidas');
  }

  logLoginDebug(rid, {
    step: 'EDGE_FUNCTION_SUCCESS',
    status: res.status,
    durationMs: Math.round(performance.now() - edgeStart),
  });

  return {
    token: json.jwt || json.token,
    jwt: json.jwt,
    refreshToken: json.refreshToken || null,
    user: json.user,
  };
}

export { adminFetch };
